import { useMutation, useQueryClient } from '@tanstack/react-query';

import { CreateQuizEventAdapter } from '~analytics/adapters/CreateQuizEventAdapter';
import {
  QuizToastActions,
  apiToaster,
} from '~teacher/components/quizzes/toastActions/';
import { increaseQuizCount } from '~teacher/hook/useQuizLimit';
import { quizzesQueryKey } from '~teacher/hook/useQuizzes';

import {
  CopyQuizBySocParams,
  ResponseBody,
  copyQuizBySoc,
} from '../../api/quiz/copyQuiz';
import { uniqueIdFactory } from '../../api/quiz/getQuizzes/normalize/uniqueIds';
import { addQuizToCache } from '.';

type CopyQuizBySocCodeParams = Pick<
  CopyQuizBySocParams,
  'parentId' | 'socNumber' | 'newName' | 'originalName'
>;

export function useCopyQuizBySoc(items: (number | string)[]) {
  const queryClient = useQueryClient();
  return useMutation(
    // Actual mutation function (first argument to useMutation) -
    // fires after onMutate and needed for isLoading, etc.
    ({
      parentId,
      socNumber,
      newName,
      originalName,
    }: CopyQuizBySocCodeParams) => {
      return copyQuizBySoc({
        parentId,
        socNumber,
        newName,
        originalName,
      });
    },

    // Mutation options (second argument to useMutation) - contains
    // onMutate, onError, onSettled, etc.
    {
      onMutate: async () => {
        await queryClient.cancelQueries(quizzesQueryKey.quiz);
      },

      onSuccess: (copyQuizBySocResp: ResponseBody) => {
        new CreateQuizEventAdapter({ creation_method: 'copy' }).sendAnalytics();
        const { date, id, name, sharable, soc, version, parentId } =
          copyQuizBySocResp;

        const uniqueId: string = uniqueIdFactory.quiz.toUniqueId(id);
        apiToaster([...items], QuizToastActions.COPY);

        addQuizToCache(queryClient, {
          id: id,
          name: name,
          parentId: parentId,
          type: 'quiz',
          uniqueId: uniqueId,
          date: new Date(date),
          soc: soc,
          deleted: false,
          version: version,
          sharable: sharable,
          standard: null,
        });
      },

      onError: (error) => {
        apiToaster([], QuizToastActions.COPY, error);
        increaseQuizCount(queryClient);
      },

      // react-query recommends always  after error or success
      onSettled: () => {
        queryClient.invalidateQueries(quizzesQueryKey.quiz);
      },
    }
  );
}
